import { CreditCard } from 'data/shared/__autogenerated__/types/CreditCard';
import { CreditCardData } from 'data/shared/CreditCardData';
import { CreditCardRewardsType } from 'data/shared/CreditCardRewardsTypes';
import { flattenRewardsEarnRates } from 'data/shared/utils/flattenRewardsEarnRates';

export type TopEarnRate = {
  category: string;
  earnRate: number;
  rewardsType: CreditCardRewardsType;
};

/**
 * Gets the top earn rates for a set of cards.
 * Filters out travel portal rates, flattens selectable/quarterly categories,
 * and returns sorted by rate (shallow categories prioritized).
 */
export function getTopEarnRatesForCards(
  cardIds: CreditCard[],
  options: { limit?: number } = {}
): TopEarnRate[] {
  const { limit = 4 } = options;

  const rateMap = new Map<
    string,
    { rate: number; rewardsType: CreditCardRewardsType }
  >();

  cardIds.forEach((cardId) => {
    const cardData = CreditCardData[cardId];
    if (!cardData?.rewards_earn_rates) {
      return;
    }

    // Filter out rates that require travel portal
    const filteredRates = cardData.rewards_earn_rates.filter(
      (rate) => !rate.requiresTravelPortal
    );

    // Flatten earn rates to handle selectable categories, quarterly categories, etc.
    const flattenedRates = flattenRewardsEarnRates(
      filteredRates,
      undefined,
      cardData.rewardsType
    );

    flattenedRates.forEach((rate) => {
      if (!rate.category || !rate.earnRate) {
        return;
      }

      const current = rateMap.get(rate.category);
      // Take the maximum rate for each category
      if (!current || rate.earnRate > current.rate) {
        rateMap.set(rate.category, {
          rate: rate.earnRate,
          rewardsType: (cardData.rewardsType || 'cash_back') as CreditCardRewardsType,
        });
      }
    });
  });

  // Get category depth for sorting
  const getCategoryDepth = (category: string): number => {
    return category.split('.').length;
  };

  return Array.from(rateMap.entries())
    .map(([category, { rate, rewardsType }]) => ({
      category,
      earnRate: rate,
      rewardsType,
      depth: getCategoryDepth(category),
    }))
    .filter((item) => item.earnRate > 1) // Only show rates > 1%
    .sort((a, b) => {
      // Prioritize categories with depth 1 or 2
      const depthA = a.depth <= 2 ? 1 : 0;
      const depthB = b.depth <= 2 ? 1 : 0;

      if (depthA !== depthB) {
        return depthB - depthA;
      }

      // Otherwise, sort by rate
      return b.earnRate - a.earnRate;
    })
    .slice(0, limit)
    .map(({ category, earnRate, rewardsType }) => ({
      category,
      earnRate,
      rewardsType,
    }));
}
